<template>
  <component
    :is="getComponent()"
    :to="to"
    :href="href"
    :target="target"
    tabindex="0"
    @keyup.enter="handleClick()"
    @click="handleClick()"
    :class="[
      'rounded-full text-center flex items-center justify-center whitespace-nowrap transition-colors duration-150 select-none',
      getPadding(),
      getStyle(),
    ]"
  >
    <div v-if="isLoading"><ion-spinner name="dots" class="-my-2" /></div>
    <template v-else>
      <ZeitIcon v-if="icon" :name="icon" :fill="iconFill" :class="[getIconSize(), '-my-2', $slots.default ? 'mr-2' : '']" />
      <slot />
      <ZeitIcon v-if="postIcon" :name="postIcon" :fill="iconFill" :class="[getIconSize(), '-my-2', $slots.default ? 'ml-2' : '']" />
    </template>
  </component>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';

  import { IonSpinner } from '@ionic/vue';

  import ZeitIcon from '@/components/ui/ZeitIcon.vue';

  export default defineComponent({
    components: {
      IonSpinner,
      ZeitIcon,
    },
    emits: ['click'],
    props: {
      disabled: {
        type: Boolean,
        default: false,
      },
      href: {
        type: String,
        default: undefined,
      },
      target: {
        type: String,
        default: undefined,
      },
      shadow: {
        type: String,
        default: 'shadow-lg',
      },
      layout: {
        type: String,
        default: 'md',
      },
      size: {
        type: String,
        default: 'sm',
      },
      icon: {
        type: String,
        default: '',
      },
      postIcon: {
        type: String,
        default: '',
      },
      fill: {
        type: String,
        default: 'full',
      },
      color: {
        type: String,
        default: 'primary',
      },
      iconFill: {
        type: String,
        default: 'solid',
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      to: {
        type: Object,
        default: undefined,
      },
    },
    methods: {
      getIconSize() {
        if (this.layout == 'rounded') return 'w-6 h-6';
        if (this.size == 'xs') return 'w-4 h-4';
        return 'w-5 h-5';
      },
      getPadding() {
        if (this.layout == 'md') return 'py-3 px-6';
        if (this.layout == 'sm') return 'py-1.5 px-3';
        if (this.layout == 'xs') return 'py-1 px-2';
        if (this.layout == 'rounded') return 'w-8 h-8';

        return this.layout;
      },
      getStyle() {
        const css: Array<string> = [];

        // Text
        css.push('font-semibold');

        // Size
        css.push('text-' + this.size);

        // Colors
        if (this.disabled) css.push('opacity-50');

        if (this.color == 'primary') {
          if (this.fill == 'full') css.push('bg-primary text-white shadow-primary/25 hover:bg-primary-dark');
          if (this.fill == 'clear') css.push('text-primary bg-primary/0 hover:bg-primary/10');
          if (this.fill == 'outline') css.push('ring-2 ring-primary text-primary hover:bg-primary/10')
        }
        if (this.color == 'zinc') {
          if (this.fill == 'full') css.push('bg-zinc-200 hover:bg-zinc-300 text-zinc-500 dark:bg-zinc-700 dark:hover:bg-zinc-800 dark:text-zinc-300')
          if (this.fill == 'clear') css.push('text-zinc-500 bg-zinc-200/0 dark:bg-zinc-800/0 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800');
          if (this.fill == 'outline') css.push('ring-2 ring-zinc-500 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-500/10')
        }

        // Pointer
        css.push(this.disabled ? 'cursor-default' : 'cursor-pointer');

        // Shadows
        if (this.fill == 'full' && !this.disabled) css.push(this.shadow);

        return css.join(' ');
      },
      getComponent() {
        if (this.to) return 'router-link';
        if (this.href) return 'a';
        return 'div';
      },
      handleClick() {
        if (this.isLoading || this.disabled) return;
        if (this.to) {
          this.$router.push(this.to);
        } else {
          this.$emit('click');
        }
      },
    }
  })
</script>
